// 🔐 COFRE DA SESSÃO DE ASSINATURA — B212 F4-sessão (AI_126) · multi-janela (B295/AI_135)
// ─────────────────────────────────────────────────────────────
// Guarda a sessão multi-assinatura do BIRD ID para o médico autorizar 1× e
// assinar várias receitas por horas (como a Memed).
//
// Fica no localStorage do navegador (NÃO em Firestore/servidor) para ser
// COMPARTILHADA entre as janelas do hepato.app: ao clicar num paciente na
// agenda, o prontuário abre em janela NOVA — e essa janela precisa enxergar a
// mesma autorização, senão o BIRD ID pediria o QR de novo a cada ficha aberta.
// (Em memória de aba, a autorização não atravessa a fronteira da janela.)
//
// 3 travas de segurança que tornam seguro guardar no navegador:
//   1) amarrada ao uid do médico que autorizou — outro médico no mesmo PC NUNCA
//      reusa a sessão (nem assina com o certificado errado);
//   2) prazo (expiraEm) — autorização vencida é descartada automaticamente;
//   3) limpa no logout (AuthContext) — não sobrevive ao fim da sessão do app.
//
// A sessão (xApiKey) é credencial de assinatura de curta duração, médico-only,
// que só assina os documentos do próprio autor (garantido também no backend:
// authFetch com ID Token + guarda de "só o autor assina").
// ─────────────────────────────────────────────────────────────

const CHAVE = "hepato_sessao_assinatura";

// Margem de segurança: descarta a sessão um pouco antes do fim real, para não
// começar uma assinatura que expira no meio.
const MARGEM_MS = 60_000;

interface SessaoAssinatura {
  /** xApiKey da sessão PSC autorizada. */
  sessao: string;
  /** Timestamp (ms) em que a sessão expira. */
  expiraEm: number;
  /** Quantas assinaturas ainda restam nesta sessão. */
  restantes: number;
  /** uid do médico que autorizou (amarração — trava nº 1). */
  uid: string;
}

/** Lê e valida o formato do cofre; remove o registro se corrompido. null se vazio/indisponível. */
function ler(): SessaoAssinatura | null {
  try {
    const cru = typeof localStorage === "undefined" ? null : localStorage.getItem(CHAVE);
    if (!cru) return null;
    const obj = JSON.parse(cru);
    if (
      !obj ||
      typeof obj.sessao !== "string" ||
      typeof obj.expiraEm !== "number" ||
      typeof obj.restantes !== "number" ||
      typeof obj.uid !== "string"
    ) {
      limparSessao();
      return null;
    }
    return obj as SessaoAssinatura;
  } catch {
    // JSON corrompido ou localStorage indisponível (modo privado/quota) → sem sessão.
    limparSessao();
    return null;
  }
}

/** Grava o cofre; em falha (quota/indisponível) degrada em silêncio (próxima assinatura pede QR). */
function gravar(s: SessaoAssinatura): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(CHAVE, JSON.stringify(s));
  } catch {
    /* no-op */
  }
}

/** Expirou (com margem) ou esgotou as assinaturas? */
function expiradaOuVazia(s: SessaoAssinatura): boolean {
  return Date.now() >= s.expiraEm - MARGEM_MS || s.restantes <= 0;
}

/** Guarda uma sessão recém-autorizada, amarrada ao médico (uid) que a autorizou. */
export function guardarSessao(
  sessao: string,
  lifetimeSegundos: number,
  numeroDocs: number,
  uid: string
): void {
  if (!sessao || !uid) return;
  gravar({
    sessao,
    expiraEm: Date.now() + lifetimeSegundos * 1000,
    restantes: Math.max(1, numeroDocs),
    uid,
  });
}

/**
 * Retorna a sessão se válida PARA ESTE médico (uid bate, não expirada, com
 * assinaturas restantes); senão null. Sem uid → null (nunca reusa às cegas).
 * Sessão de OUTRO médico nunca é reusada (trava nº 1).
 */
export function obterSessaoValida(uid?: string | null): string | null {
  if (!uid) return null;
  const s = ler();
  if (!s) return null;
  if (expiradaOuVazia(s)) {
    limparSessao();
    return null;
  }
  // Autorização de outro médico — não reusa (e não apaga: o dono pode estar
  // usando; será sobrescrita quando o dono atual reautorizar, ou limpa no logout).
  if (s.uid !== uid) return null;
  return s.sessao;
}

/** Desconta uma assinatura da sessão (chamar após assinar com sucesso). Cross-janela: lê do disco, desconta, regrava. */
export function consumirUmaAssinatura(): void {
  const s = ler();
  if (!s) return;
  s.restantes -= 1;
  if (expiradaOuVazia(s)) {
    limparSessao();
    return;
  }
  gravar(s);
}

/** Esvazia o cofre (ex.: sessão expirou na BRy, ou logout). */
export function limparSessao(): void {
  // B343-FU: o link pendente segue a mesma vida do cofre (some no logout).
  limparLinkPendente();
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(CHAVE);
  } catch {
    /* no-op */
  }
}

/** Info p/ UI (assinaturas restantes e quando expira) para o médico atual; null se não há sessão válida dele. */
export function infoSessao(uid?: string | null): { restantes: number; expiraEm: number } | null {
  if (!uid) return null;
  const s = ler();
  if (!s || expiradaOuVazia(s) || s.uid !== uid) return null;
  return { restantes: s.restantes, expiraEm: s.expiraEm };
}

// ─────────────────────────────────────────────────────────────
// 🔗 B343-FU (AI_150) — LINK DE AUTORIZAÇÃO PENDENTE
//
// Cada chamada a `psc/link` abre uma sessão NOVA no certificado do médico (12h,
// 50 documentos) e nós não temos como revogá-la (a BRy não documenta endpoint
// para isso — pergunta em aberto no chamado). No uso normal isso não pesa: o
// cofre acima reaproveita a sessão AUTORIZADA e o link só é pedido uma vez por
// plantão.
//
// O problema aparece na FALHA: se a autorização não conclui (foi o caso de
// 04/08, com o BirdID devolvendo 500), nada chega ao cofre, e cada "Tentar de
// novo" pede outro link — dezenas de sessões abertas numa tarde. Aqui guardamos
// o link PENDENTE enquanto a janela de autorização dele está de pé, para que a
// nova tentativa reaproveite o mesmo QR em vez de abrir mais uma sessão.
//
// Mesmas travas do cofre: amarrado ao uid, com prazo, e apagado no logout.
// Vive no localStorage pelo mesmo motivo do cofre — a agenda abre o prontuário
// em janela nova, e sem isso cada janela pediria o seu próprio link.
// ─────────────────────────────────────────────────────────────

const CHAVE_PENDENTE = "hepato_link_assinatura_pendente";

interface LinkPendente {
  authUrl: string;
  /** xApiKey da sessão ainda NÃO autorizada. */
  sessao: string;
  /** Timestamp (ms) do fim da janela de autorização. */
  expiraEm: number;
  uid: string;
  /** Guardados junto porque o cofre precisa deles quando a autorização conclui. */
  lifetime: number;
  numberOfDocuments: number;
}

/** Guarda o link recém-pedido para que uma nova tentativa o reaproveite. */
export function guardarLinkPendente(
  authUrl: string,
  sessao: string,
  janelaSegundos: number,
  uid: string | null | undefined,
  lifetime: number,
  numberOfDocuments: number
): void {
  if (!authUrl || !sessao || !uid) return;
  try {
    const dados: LinkPendente = {
      authUrl,
      sessao,
      expiraEm: Date.now() + janelaSegundos * 1000,
      uid,
      lifetime,
      numberOfDocuments,
    };
    localStorage.setItem(CHAVE_PENDENTE, JSON.stringify(dados));
  } catch {
    /* localStorage indisponível → apenas não reaproveita (pede link novo) */
  }
}

/**
 * Link ainda dentro da janela de autorização, do MESMO médico. null quando não
 * há, venceu ou é de outro uid — nesses casos o chamador pede um link novo.
 */
export function obterLinkPendente(
  uid?: string | null
): {
  authUrl: string;
  sessao: string;
  expiraEm: number;
  lifetime: number;
  numberOfDocuments: number;
} | null {
  try {
    const cru = localStorage.getItem(CHAVE_PENDENTE);
    if (!cru) return null;
    const d = JSON.parse(cru) as LinkPendente;
    if (
      !d ||
      typeof d.authUrl !== "string" ||
      typeof d.sessao !== "string" ||
      typeof d.expiraEm !== "number" ||
      typeof d.uid !== "string"
    ) {
      localStorage.removeItem(CHAVE_PENDENTE);
      return null;
    }
    // Trava do médico: nunca entrega o link de outro (PC compartilhado).
    if (!uid || d.uid !== uid) return null;
    // Margem: não devolve link que morre no meio da autorização.
    if (d.expiraEm - MARGEM_MS <= Date.now()) {
      localStorage.removeItem(CHAVE_PENDENTE);
      return null;
    }
    return {
      authUrl: d.authUrl,
      sessao: d.sessao,
      expiraEm: d.expiraEm,
      lifetime: Number(d.lifetime) || 0,
      numberOfDocuments: Number(d.numberOfDocuments) || 0,
    };
  } catch {
    return null;
  }
}

/** Descarta o link pendente (autorizou, desistiu, ou a BRy o recusou). */
export function limparLinkPendente(): void {
  try {
    localStorage.removeItem(CHAVE_PENDENTE);
  } catch {
    /* nada a fazer */
  }
}
